import CustomTooltip from '../../../shared/CustomTooltip';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import { Grid, Stack, Typography, useTheme } from '@mui/material';
import React, { useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import * as z from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';
import { useForm } from 'react-hook-form';
import { CustomButton } from '../../../../layout/styledcomponents/CustomButton';
import LoadingComponent from '../../../../layout/styledcomponents/LoadingAnimations/LoadingComponent';
import UserRole from '../../../../shared/AuthRoles';
import { handleHttpError } from '../../../../shared/utils';
import FormTextField from '../../../shared/FormTextField';
import toast from '../../../shared/Toast';
import {
    useAddXeroNetworkKeyMutation,
    useGetXeroNetworkKeyQuery,
} from '../../../../app/Slices/SevenDayApiSlice';

const XeroNetworkKeySettings = () => {
    const loggedInUser = UserRole();
    const navigate = useNavigate();
    const theme = useTheme();

    useEffect(() => {
        if (loggedInUser?.role.includes('guest')) {
            navigate(`/permissionDenied`);
        }
    }, []);

    const { data: xeroSettings, isLoading } = useGetXeroNetworkKeyQuery(
        `/owners/${loggedInUser?.company}/xero`,
    );

    const [addXeroNetworkkKey] = useAddXeroNetworkKeyMutation();

    const schema = z.object({
        xeroNetworkKey: z.coerce.string(),
    });

    type XeroNetoworkKeySettings = z.infer<typeof schema>;
    const {
        reset,
        control,
        handleSubmit,
        formState: { isSubmitting },
    } = useForm<XeroNetoworkKeySettings>({
        resolver: zodResolver(schema),
    });

    useEffect(() => {
        if (xeroSettings) {
            const { xeroNetworkKey } = xeroSettings;
            reset({ xeroNetworkKey });
        }
    }, [xeroSettings]);

    const onSubmit = async (data: XeroNetoworkKeySettings) => {
        const requestData = [loggedInUser?.company, data];

        addXeroNetworkkKey(requestData)
            .then((data: any) => {
                if (data?.error) {
                    const errorMessage = handleHttpError(data?.error);
                    toast.error(errorMessage);
                } else {
                    toast.success(`Successfully added network key settings`);
                }
            })
            .catch(() => {
                toast.error(
                    'Failed to add network key to settings. Please try again.',
                );
            });
    };

    return isLoading ? (
        <LoadingComponent />
    ) : (
        <form onSubmit={handleSubmit(onSubmit)}>
            <Grid
                sx={{
                    width: '100%',
                    height: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    gap: '40px',
                }}
            >
                <Grid
                    sx={{
                        width: '100%',
                        justifyContent: 'left',
                        display: 'flex',
                    }}
                >
                    <Stack spacing={2}>
                        <Typography
                            variant='h1'
                            sx={{
                                textAlign: 'left',
                                textTransform: 'uppercase',
                            }}
                        >
                            Xero Network Key Settings
                        </Typography>

                        <Typography
                            color='text.primary'
                            sx={{
                                fontSize: '14px',
                            }}
                        >
                            The following network key can be retrieved from your{' '}
                            <Link
                                style={{
                                    textDecoration: 'underline',
                                }}
                                target='_blank'
                                to={
                                    'https://login.xero.com/identity/user/login?'
                                }
                            >
                                Xero account.
                            </Link>
                        </Typography>
                    </Stack>
                </Grid>

                <Grid container spacing={2}>
                    <Grid item container columns={25} spacing={1}>
                        <Grid item xs={12} lg={5} sx={{ gap: '2px' }}>
                            <Grid sx={{ display: 'flex', gap: '2px' }}>
                                <Typography color='text.primary'>
                                    Network Key
                                </Typography>

                                <CustomTooltip
                                    title={
                                        <Typography>
                                            Give 7DAY your network key so we can
                                            send you invoices or bills through
                                            the Xero to Xero network. Your Xero
                                            network key is available from the
                                            Organisation settings screen within
                                            your Xero Account.
                                            <ul>
                                                <li>
                                                    Log into your Xero Account.
                                                </li>
                                                <li>
                                                    Click the organisation name,
                                                    then select Settings.
                                                </li>
                                                <li>
                                                    Under Features, click Xero
                                                    To Xero.
                                                </li>
                                                <li>
                                                    Next to Your Xero Network
                                                    Key, click Copy.
                                                </li>
                                            </ul>
                                            <Link
                                                style={{
                                                    color: 'white',
                                                    textDecoration: 'underline',
                                                }}
                                                target='_blank'
                                                to={
                                                    'https://central.xero.com/s/article/Send-your-Xero-network-key-to-another-Xero-organisation'
                                                }
                                            >
                                                For more information visit here
                                            </Link>
                                        </Typography>
                                    }
                                    placement='right'
                                >
                                    <HelpOutlineIcon
                                        sx={{
                                            color: 'text.primary',
                                            fontSize: '16px',
                                        }}
                                    />
                                </CustomTooltip>
                            </Grid>
                            <Grid>
                                {' '}
                                <Typography
                                    color='text.primary'
                                    sx={{ fontSize: '14px' }}
                                >
                                    Status:{' '}
                                    <span
                                        style={{
                                            color: xeroSettings?.xeroNetworkKey
                                                ? theme.palette.textAccent.main
                                                : 'red',
                                        }}
                                    >
                                        {xeroSettings?.xeroNetworkKey
                                            ? 'Connected'
                                            : 'Not Connected'}
                                    </span>
                                </Typography>
                            </Grid>
                        </Grid>

                        <Grid item xs={25} lg={9}>
                            <FormTextField
                                placeholder={xeroSettings?.xeroNetworkKey}
                                type='string'
                                name='xeroNetworkKey'
                                control={control}
                            />
                        </Grid>
                    </Grid>

                    <Grid item container columns={25} spacing={1}>
                        <Grid
                            item
                            xs={25}
                            lg={14}
                            sx={{
                                display: 'flex',
                                justifyContent: 'flex-end',
                            }}
                        >
                            <CustomButton
                                type='submit'
                                variant='contained'
                                color='primary'
                                loading={isSubmitting}
                                disabled={isSubmitting}
                            >
                                Save
                            </CustomButton>
                        </Grid>
                    </Grid>
                </Grid>
            </Grid>
        </form>
    );
};

export default XeroNetworkKeySettings;
